import { Chip, TextField, Tooltip, Typography } from "@material-ui/core"
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined"
import PropTypes from "prop-types"
import React, { useContext, useEffect } from "react"
import { useQueryClient } from "react-query"
import { Link } from "react-router-dom"
import { useDebounce } from "use-lodash-debounce"
import AutocompleteInput from "~/components/Common/Autocomplete"
import CounterHeader from "~/components/Common/CounterHeader"
import DateTime from "~/components/Common/DateTime"
import Tag from "~/components/Common/Tag"
import ThumbnailUpload from "~/components/Common/ThumbnailUpload"
import AppContext from "~/contexts/App"
import Course from "~/contexts/Course"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"
import serialize from "~/store/serialize"
import preCacheCloudinaryImages from "~/utils/preCacheCloudinaryImages"

import Box from "~/components/Common/Box"
import useStyles from "./styles"

const CourseDetails = ({ tags }) => {
  const classes = useStyles()
  const queryClient = useQueryClient()

  const {
    setDebouncedDetails,
    debouncedDetails,
    course,
    trySave,
    setTagsContext,
    tagsContext,
    updateCourseMutation,
    lengthError,
    tryPublish,
    courseDetailsValues,
    setCourseDetailsValues,
    setInitialDetailsContext,
    levelValue,
    setLevelValue,
    unsavedState,
  } = useContext(Course)
  const { openSnackBar, setPageSubTitle } = useContext(AppContext)
  const debouncedLocalValues = useDebounce(courseDetailsValues, 200)

  const openErrorSnackbar = useOpenErrorSnackbar()

  const [levelState, setLevelState] = React.useState({
    fundamental: false,
    operational: false,
    technical: false,
    cloudPerformance: false,
    orchestration: false,
  })

  const {
    fundamental,
    operational,
    technical,
    cloudPerformance,
    orchestration,
  } = levelState
  //show errors if inputs are empty or more than allow characters
  const descriptionError =
    ((debouncedDetails?.description === "" ||
      debouncedDetails?.description?.length > 2000) &&
      tryPublish) ||
    (lengthError && debouncedDetails?.description?.length > 2000)

  const nameError =
    ((debouncedDetails?.name === "" || debouncedDetails?.name?.length > 255) &&
      (trySave || tryPublish)) ||
    (lengthError && debouncedDetails?.name?.length > 255)

  const headlineError =
    ((debouncedDetails?.headline === "" ||
      debouncedDetails?.headline?.length > 255) &&
      tryPublish) ||
    (lengthError && debouncedDetails?.headline?.length > 255)

  // disable if: published, unpublished, or archived - only available to draft
  const disablePicker =
    course?.published === 1 ||
    (course?.published === 0 &&
      course?.has_been_published === 1 &&
      course?.archived === 0) ||
    (course?.archived === 1 && course?.published === 0)

  const setThumbnail = (uploadRes, loadingAction, type) => {
    const image =
      uploadRes === null
        ? null
        : {
            url: uploadRes.data.url,
            secure_url: uploadRes.data.secure_url,
            originial_filename: uploadRes.data.original_filename,
            version: uploadRes.data.version,
            format: uploadRes.data.format,
            public_id: uploadRes.data.public_id,
          }
    const data = {
      [type]: image === null ? null : JSON.stringify(image),
    }
    updateCourse(data, image, type, loadingAction)
  }

  const updateCourse = (data, image, type, loadingAction) => {
    updateCourseMutation.mutate(data, {
      onSuccess: (res) => {
        serialize("course", res.data).then((serializedData) => {
          queryClient.setQueryData(["course", res.data.id], (oldData) => {
            oldData[type] = serializedData[type]
            return oldData
          })
        })
        setTimeout(() => {
          loadingAction(false)
        }, 400)
        serialize("course", res.data).then((serializedData) => {
          return queryClient.setQueryData("courses", (oldData) => {
            let newArray = []
            newArray = oldData.map((x) => {
              if (x.id !== serializedData.id) {
                return x
              } else {
                return serializedData
              }
            })
            return [...newArray]
          })
        })
        // pre-caches all sizes for the thumbnail
        if (image != null) {
          preCacheCloudinaryImages(image)
        }
      },
      onError: (err) => {
        loadingAction(false)
        openErrorSnackbar(err, "Error Updating Hero Image")
      },
    })
  }

  //set course subtitle to empty string
  useEffect(() => {
    setPageSubTitle("")
  }, [])

  //delete tag from the tags array in course context
  const deleteTag = (name) => {
    setTagsContext(tagsContext?.filter((x) => x.name !== name))
  }

  //change level option
  const handleChangeLevel = (event) => {
    setLevelState({
      fundamental: event.target.name === "fundamental" ? true : false,
      operational: event.target.name === "operational" ? true : false,
      technical: event.target.name === "technical" ? true : false,
      cloudPerformance:
        event.target.name === "cloud-performance" ? true : false,
      orchestration: event.target.name === "orchestration" ? true : false,
    })
    setLevelValue(event.target.name)
  }

  // set course details to component state
  useEffect(() => {
    if (course?.id) {
      const courseData = {
        name: course.name,
        description: course.description || "",
        headline: course.headline || "",
        is_public: Boolean(course.is_public) ?? false,
        show_latest_version: Boolean(course.show_latest_version) ?? false,
        disable_learn: Boolean(course.disable_learn) ?? false,
        discover_page: Boolean(course.discover_page) ?? false,
        mailgun_template_id: course.mailgun_template_id || "",
        mailgun_subject: course.mailgun_subject || "",
        mailgun_data: course.mailgun_data || "",
        promoted: Boolean(course.promoted) ?? false,
        course_type: course.course_type || "course",
        facebook_sso_required: Boolean(course.facebook_sso_required) ?? false,
        meta_quest_sso_required:
          Boolean(course.meta_quest_sso_required) ?? false,
        google_sso_required: Boolean(course.google_sso_required) ?? false,
        disable_final_assessment:
          Boolean(course.disable_final_assessment) ?? false,
        hide_assessment_score: Boolean(course.hide_assessment_score) ?? false,
        course_cover_mp4: course.course_cover_mp4 || null,
        course_cover_webm: course.course_cover_webm || null,
        vaas_logo_redirect: course.vaas_logo_redirect || "",
      }
      setCourseDetailsValues({ ...courseData })
      setInitialDetailsContext({ ...courseData })
    }
  }, [course])

  //set initial values to level
  useEffect(() => {
    setLevelState({
      fundamental: course?.level === "fundamental" ? true : false,
      operational: course?.level === "operational" ? true : false,
      technical: course?.level === "technical" ? true : false,
      cloudPerformance: course?.level === "cloud-performance" ? true : false,
      orchestration: course?.level === "orchestration" ? true : false,
    })
  }, [course])

  const getUploadData = (uploadRes) => {
    if (uploadRes === null) {
      return null
    } else {
      return JSON.stringify({
        url: uploadRes.data.url,
        secure_url: uploadRes.data.secure_url,
        original_filename: uploadRes.data.original_filename,
        version: uploadRes.data.version,
        format: uploadRes.data.format,
        public_id: uploadRes.data.public_id,
      })
    }
  }
  //set details context state from this component state using debounce
  useEffect(() => {
    setDebouncedDetails(courseDetailsValues)
  }, [debouncedLocalValues])

  const uploadDataMP4Func = (uploadData) => {
    setCourseDetailsValues((oldState) => ({
      ...oldState,
      course_cover_mp4: uploadData,
    }))
    return
  }

  const uploadDataWEBMFunc = (uploadData) => {
    setCourseDetailsValues((oldState) => ({
      ...oldState,
      course_cover_webm: uploadData,
    }))
    return
  }

  return (
    <Box className={classes.container}>
      <Typography variant="h3" color="primary" className={classes.title}>
        Course Details
      </Typography>
      <div className={classes.twoFieldContainer}>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <CounterHeader
            title="Course Name"
            value={debouncedDetails?.name}
            max={255}
          />
          <TextField
            value={courseDetailsValues?.name || ""}
            onChange={(e) => {
              setCourseDetailsValues((oldState) => ({
                ...oldState,
                name: e.target.value,
              }))
            }}
            style={{ marginBottom: "10px" }}
            InputProps={{
              classes: {
                root: classes.inputBackgroundColor,
              },
            }}
            variant="filled"
            size="small"
            fullWidth
          />
          {nameError ? (
            <Typography className={classes.requiredText}>
              Name is required and it has to be less than 256 characters
            </Typography>
          ) : null}
        </Box>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <CounterHeader
            title="Headline"
            value={debouncedDetails?.headline}
            max={255}
          />
          <TextField
            value={courseDetailsValues?.headline || ""}
            onChange={(e) => {
              setCourseDetailsValues((oldState) => ({
                ...oldState,
                headline: e.target.value,
              }))
            }}
            style={{ marginBottom: "10px" }}
            InputProps={{
              classes: {
                root: classes.inputBackgroundColor,
              },
            }}
            variant="filled"
            size="small"
            fullWidth
          />
          {headlineError ? (
            <Typography className={classes.requiredText}>
              Headline is required and it has to be less than 255 characters
            </Typography>
          ) : null}
        </Box>
      </div>
      <div className={classes.twoFieldContainer}>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <CounterHeader
            title="Course Description"
            value={debouncedDetails?.description}
            max={2000}
          />
          <TextField
            value={courseDetailsValues.description || ""}
            multiline
            maxRows={10}
            onChange={(e) => {
              setCourseDetailsValues((oldState) => ({
                ...oldState,
                description: e.target.value,
              }))
            }}
            InputProps={{
              disableUnderline: true,
              classes: {
                root: classes.inputDescription,
              },
            }}
            size="small"
            fullWidth
          />
          {descriptionError ? (
            <Typography className={classes.requiredText}>
              Description is required and it has to be less than 2000 characters
            </Typography>
          ) : null}
        </Box>
        <Box width={"100%"} className={classes.dateTime}>
          <DateTime disablePicker={disablePicker} />
        </Box>
      </div>
      <div className={classes.twoFieldContainer}>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <Typography color="secondary" className={classes.inputLabel}>
            Search Terms
          </Typography>
          <AutocompleteInput
            id="tabs-autocomplete"
            group={tags}
            context={tagsContext}
            setContext={setTagsContext}
          />

          <Box
            display="flex"
            flexWrap="wrap"
            style={{ height: "100%" }}
            marginBottom="16px"
          >
            {tagsContext?.map((x, i) => {
              return (
                <Tag
                  key={i}
                  tagName={x.name || x}
                  deleteTag={() => deleteTag(x.name)}
                />
              )
            })}
          </Box>
        </Box>

        {/* <Box display="flex" flexDirection={"column"} width={"100%"}>
          <Typography color="secondary" className={classes.inputLabel}>
            Level
          </Typography>
          <FormGroup style={{ display: "flex", flexDirection: "row" }}>
            <FormControlLabel
              control={
                <Radio
                  checked={fundamental}
                  onChange={handleChangeLevel}
                  name="fundamental"
                />
              }
              label="Fundamental"
            />
            <FormControlLabel
              control={
                <Radio
                  checked={operational}
                  onChange={handleChangeLevel}
                  name="operational"
                />
              }
              label="Operational"
            />
            <FormControlLabel
              control={
                <Radio
                  checked={technical}
                  onChange={handleChangeLevel}
                  name="technical"
                />
              }
              label="Technical"
            />
            <FormControlLabel
              control={
                <Radio
                  checked={cloudPerformance}
                  onChange={handleChangeLevel}
                  name="cloud-performance"
                />
              }
              label="Cloud Performance"
            />
            <FormControlLabel
              control={
                <Radio
                  checked={orchestration}
                  onChange={handleChangeLevel}
                  name="orchestration"
                />
              }
              label="Orchestration"
            />
          </FormGroup>
        </Box> */}
      </div>

      <Typography color="secondary" className={classes.inputLabel}>
        Categories
      </Typography>
      <Box
        display="flex"
        flexWrap="wrap"
        style={{ height: "100%" }}
        marginBottom="16px"
      >
        {course?.categories.map((category, i) => {
          return (
            <Link
              key={i}
              to={`/categories/${category.id}`}
              className="mt-2 mr-2"
            >
              <Chip
                label={category.name}
                size="small"
                color="primary"
                className="cursor-pointer"
              />
            </Link>
          )
        })}
      </Box>
      <Box className={classes.thumbnailSectionContainer}>
        <Box className={classes.thumbnailContainer}>
          <Typography color="secondary" className={classes.inputLabel}>
            Course Cover Image{" "}
            <Tooltip title="The primary image shown for a course">
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <ThumbnailUpload
            title="Drop course cover image here"
            cloudinaryUploadPreset={
              import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
            }
            value={course?.thumbnail}
            onUpload={setThumbnail}
            type="thumbnail"
          />
          {Object.keys(course?.thumbnail)?.length === 0 && tryPublish ? (
            <Typography className={classes.requiredText}>
              Course Cover Image is required
            </Typography>
          ) : null}
          {Boolean(course?.thumbnail || false) && (
            <Typography variant="h4" className={classes.mediaKey}>
              {course?.thumbnail.originial_filename}.{course?.thumbnail.format}
            </Typography>
          )}
        </Box>
        {/* <Box className={classes.thumbnailContainer}>
          <Typography color="secondary" className={classes.inputLabel}>
            Course Gif
            <Tooltip title="A Gif that shows when hovering a course cover">
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <ThumbnailUpload
            title="Drop course gif here"
            cloudinaryUploadPreset={
              import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
            }
            value={course?.cover_image}
            type="cover_image"
            onUpload={setThumbnail}
          />
          {Boolean(course?.cover_image) && (
            <Typography variant="h4" className={classes.mediaKey}>
              {course?.cover_image.originial_filename}.
              {course?.cover_image.format}
            </Typography>
          )}
        </Box> */}
      </Box>
      {/* <Box className={classes.thumbnailSectionContainer}>
        <Box className={classes.thumbnailContainer} marginTop="16px">
          <Typography color="secondary" className={classes.inputLabel}>
            Course Hero Image
            <Tooltip title="The image that shows in the background on the course details page">
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <ThumbnailUpload
            title="Drop course cover image here"
            cloudinaryUploadPreset={
              import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
            }
            value={course?.background_image}
            onUpload={setThumbnail}
            type="background_image"
            noCrop={true}
          />
          {Object.keys(course?.background_image)?.length === 0 && tryPublish ? (
            <Typography className={classes.requiredText}>
              Cover Image is required
            </Typography>
          ) : null}
          {Boolean(course?.background_image || false) && (
            <Typography variant="h4" className={classes.mediaKey}>
              {course?.background_image.originial_filename}.
              {course?.background_image.format}
            </Typography>
          )}
        </Box>
        <Box className={classes.thumbnailContainer} marginTop="16px">
          <Typography color="secondary" className={classes.inputLabel}>
            Course Cover MP4
            <Tooltip title="The video that shows upon hovering over a course">
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <CloudinaryVideoUpload
            style={{ marginBottom: "10px" }}
            value={JSON.parse(course?.course_cover_mp4)}
            type="course_cover_mp4"
            typeExpected={"mp4"}
            onUpload={(result) => {
              let uploadData = getUploadData(result)
              uploadDataMP4Func(uploadData)
            }}
          />
          {Boolean(course?.course_cover_mp4 || false) && (
            <Typography variant="h4" className={classes.mediaKey}>
              {JSON.parse(course?.course_cover_mp4).original_filename}.
              {JSON.parse(course?.course_cover_mp4).format}
            </Typography>
          )}
        </Box>
      </Box> */}
      {/* <Box className={classes.thumbnailContainer} marginTop="16px">
        <Typography color="secondary" className={classes.inputLabel}>
          Course Cover Webm
          <Tooltip title="The video that shows upon hovering over a course">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Typography>
        <CloudinaryVideoUpload
          style={{ marginBottom: "10px" }}
          value={JSON.parse(course?.course_cover_webm)}
          type="course_cover_webm"
          typeExpected={"webm"}
          onUpload={(result) => {
            let uploadData = getUploadData(result)
            uploadDataWEBMFunc(uploadData)
          }}
        />
        {Boolean(course?.course_cover_webm || false) && (
          <Typography variant="h4" className={classes.mediaKey}>
            {JSON.parse(course?.course_cover_webm).original_filename}.
            {JSON.parse(course?.course_cover_webm).format}
          </Typography>
        )}
      </Box> */}
    </Box>
  )
}

CourseDetails.propTypes = {
  courseId: PropTypes.string,
  tags: PropTypes.array,
}

export default CourseDetails
